/*
 * Выгрузка списка договоров юриста в XLSX и текст счётчика на странице.
 *
 * Почему на клиенте, а не отдельным API:
 * фильтры колонок живут в AG Grid (community, без Excel-export).
 * «Попавшие под фильтр» договоры — это уже отфильтрованные строки грида,
 * а не повторный запрос на backend. Год по-прежнему берётся из GET /orders/.
 *
 * Почему свой минимальный XLSX, а не SheetJS/ExcelJS:
 * в кабинете юриста не было ни одной Excel-зависимости, а выгрузка — одна
 * таблица строк. XLSX — это ZIP с набором XML (Office Open XML). Сжимаем
 * методом STORE (без deflate): Excel и LibreOffice это принимают, CRC32
 * для ZIP всё равно обязателен.
 */

// Предрасчёт таблицы CRC-32 (полином ZIP/PKZIP 0xEDB88320).
const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let crc = i;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 1) ? (0xEDB88320 ^ (crc >>> 1)) : (crc >>> 1);
    }
    table[i] = crc >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

function u16(value) {
  const bytes = new Uint8Array(2);
  new DataView(bytes.buffer).setUint16(0, value, true);
  return bytes;
}

function u32(value) {
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value, true);
  return bytes;
}

function concatBytes(chunks) {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    out.set(chunk, offset);
    offset += chunk.length;
  });
  return out;
}

// ZIP без сжатия: local file headers + данные + central directory + EOCD.
// Сигнатуры little-endian: 0x04034b50, 0x02014b50, 0x06054b50.
function zipStore(files) {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(({ name, content }) => {
    const nameBytes = encoder.encode(name);
    const data = typeof content === 'string' ? encoder.encode(content) : content;
    const checksum = crc32(data);
    const localHeader = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(checksum),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
    ]);

    localParts.push(localHeader, data);
    centralParts.push(concatBytes([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(checksum),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes,
    ]));
    offset += localHeader.length + data.length;
  });

  const centralDirectory = concatBytes(centralParts);
  const endOfCentralDirectory = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDirectory.length),
    u32(offset),
    u16(0),
  ]);

  return concatBytes([...localParts, centralDirectory, endOfCentralDirectory]);
}

// XML 1.0 запрещает большинство управляющих символов (кроме TAB/LF/CR).
// Фильтр через charCode, а не regex: ESLint ругается на control-char в regexp.
function stripInvalidXmlChars(value) {
  return Array.from(String(value)).filter((ch) => {
    const code = ch.charCodeAt(0);
    return code === 0x09 || code === 0x0A || code === 0x0D || code >= 0x20;
  }).join('');
}

function escapeXml(value) {
  return stripInvalidXmlChars(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// 0 → A, 25 → Z, 26 → AA. Нужно для атрибута r="A1" в ячейках листа.
function columnLetter(index) {
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

// inlineStr — строка прямо в ячейке, без sharedStrings.xml.
// xml:space="preserve" сохраняет ведущие/хвостовые пробелы, если они есть.
function cellXml(rowNumber, colIndex, value) {
  const ref = `${columnLetter(colIndex)}${rowNumber}`;
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function worksheetXml(headers, rows) {
  const allRows = [headers, ...rows];
  const sheetRows = allRows.map((values, rowIndex) => {
    const rowNumber = rowIndex + 1;
    const cells = values.map((value, colIndex) => cellXml(rowNumber, colIndex, value ?? '')).join('');
    return `<row r="${rowNumber}">${cells}</row>`;
  }).join('');

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
    + `<sheetData>${sheetRows}</sheetData>`
    + '</worksheet>'
  );
}

export function getOrdersCountText({ year, total, displayed }) {
  if (displayed !== total) {
    return `Договоров отображено: ${displayed} из ${total}`;
  }
  return `Договоров всего: ${total}`;
}

// ТЗ: «имя файла — текущая дата». Русский формат ДД.ММ.ГГГГ.
export function buildOrdersXlsxFilename(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}.${month}.${year}.xlsx`;
}

export function getColumnExportValue(col, data) {
  if (typeof col.valueGetter === 'function') {
    const value = col.valueGetter({ data });
    return value == null ? '' : String(value);
  }
  const value = data?.[col.field];
  return value == null ? '' : String(value);
}

export function getOrderExportHeaders(colDefs) {
  return colDefs.map((col) => col.headerName || col.field || '');
}

// Берём строки после фильтра и сортировки грида — то, что юрист видит на экране.
export function getOrderExportRowsFromGrid(api, colDefs) {
  const rows = [];
  if (!api || typeof api.forEachNodeAfterFilterAndSort !== 'function') {
    return rows;
  }
  api.forEachNodeAfterFilterAndSort((node) => {
    if (!node?.data) {
      return;
    }
    rows.push(colDefs.map((col) => getColumnExportValue(col, node.data)));
  });
  return rows;
}

// Минимальный пакет OOXML: типы, связи, книга, один лист. styles.xml не нужен.
export function buildOrdersXlsxBytes(headers, rows) {
  const sheetName = 'Договоры';
  return zipStore([
    {
      name: '[Content_Types].xml',
      content: (
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
        + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
        + '<Default Extension="xml" ContentType="application/xml"/>'
        + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
        + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
        + '</Types>'
      ),
    },
    {
      name: '_rels/.rels',
      content: (
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
        + '</Relationships>'
      ),
    },
    {
      name: 'xl/workbook.xml',
      content: (
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
        + '<sheets>'
        + `<sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/>`
        + '</sheets>'
        + '</workbook>'
      ),
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: (
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
        + '</Relationships>'
      ),
    },
    {
      name: 'xl/worksheets/sheet1.xml',
      content: worksheetXml(headers, rows),
    },
  ]);
}

export function downloadOrdersXlsx(headers, rows, date = new Date()) {
  const filename = buildOrdersXlsxFilename(date);
  const bytes = buildOrdersXlsxBytes(headers, rows);
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return filename;
}
